/**
 * Modal that shows a calendar-style breakdown of leave days:
 * which days are counted, which are weekends, which are public holidays, and net days.
 */
import React, { useMemo } from 'react';
import { toDateKey, publicHolidayDateSet, getDaysBreakdown } from '../utils/workingDays';
import './DaysTakenBreakdownModal.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function formatLongDate(d) {
  return typeof d === 'string' ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }) : d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

function DaysTakenBreakdownModal({
  show,
  onClose,
  startDate,
  endDate,
  publicHolidays = [],
  daysTaken = 0,
  employeeWorkingSettings = null,
  overrideWindows = null,
}) {
  const { breakdownByMonth, publicHolidayNameMap } = useMemo(() => {
    if (!startDate || !endDate) return { breakdownByMonth: [], publicHolidayNameMap: null };
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return { breakdownByMonth: [], publicHolidayNameMap: null };

    const phSet = publicHolidayDateSet(publicHolidays);
    const nameMap = new Map();
    (publicHolidays || []).forEach((h) => {
      if (typeof h !== 'object' || h == null || h.date == null) return;
      const startH = new Date(h.date);
      startH.setHours(0, 0, 0, 0);
      const endH = h.endDate ? new Date(h.endDate) : new Date(h.date);
      endH.setHours(0, 0, 0, 0);
      const label = h.name || h.nameFr || 'Public holiday';
      const d = new Date(startH);
      while (d <= endH) {
        nameMap.set(toDateKey(d), label);
        d.setDate(d.getDate() + 1);
      }
    });

    const breakdown = getDaysBreakdown(start, end, phSet, nameMap, employeeWorkingSettings, overrideWindows);
    const byMonth = {};
    breakdown.forEach((item) => {
      const y = item.date.getFullYear();
      const m = item.date.getMonth();
      const key = `${y}-${m}`;
      if (!byMonth[key]) byMonth[key] = { year: y, month: m, days: [] };
      byMonth[key].days.push(item);
    });
    const months = Object.keys(byMonth).sort().map((k) => byMonth[k]);
    return { breakdownByMonth: months, publicHolidayNameMap: nameMap };
  }, [startDate, endDate, publicHolidays, employeeWorkingSettings, overrideWindows]);

  if (!show) return null;

  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;
  const rangeLabel = start && end && !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())
    ? `${formatLongDate(start)} – ${formatLongDate(end)}`
    : '';

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="daysBreakdownModalTitle"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg" role="document" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="daysBreakdownModalTitle">
              <i className="bi bi-calendar3 me-2" /> Leave days breakdown
            </h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <p className="text-muted mb-3">
              You are requesting leave from <strong>{rangeLabel}</strong>. Below are the days in your range and how they are counted.
            </p>

            <div className="d-flex flex-wrap gap-3 mb-3 small">
              <span><span className="days-breakdown-legend days-breakdown-counted" /> Counted (working day)</span>
              <span><span className="days-breakdown-legend days-breakdown-weekend" /> Weekend (not counted)</span>
              <span><span className="days-breakdown-legend days-breakdown-holiday" /> Public holiday (not counted)</span>
              {employeeWorkingSettings != null && (
                <span><span className="days-breakdown-legend days-breakdown-nonworking" /> Your non-working day (not counted)</span>
              )}
            </div>

            <div className="days-breakdown-calendars">
              {breakdownByMonth.map(({ year, month, days }) => {
                const firstDay = new Date(year, month, 1);
                const lastDay = new Date(year, month + 1, 0);
                const startOfWeek = firstDay.getDay();
                const daysInMonth = lastDay.getDate();
                const dayMap = {};
                days.forEach((d) => { dayMap[d.date.getDate()] = d; });
                const monthName = firstDay.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
                return (
                  <div key={`${year}-${month}`} className="days-breakdown-month mb-4">
                    <h6 className="mb-2">{monthName}</h6>
                    <div className="days-breakdown-grid">
                      {WEEKDAYS.map((wd) => (
                        <div key={wd} className="days-breakdown-cell days-breakdown-header">{wd}</div>
                      ))}
                      {Array.from({ length: startOfWeek }, (_, i) => (
                        <div key={`empty-${i}`} className="days-breakdown-cell days-breakdown-empty" />
                      ))}
                      {Array.from({ length: daysInMonth }, (_, i) => {
                        const dayNum = i + 1;
                        const info = dayMap[dayNum];
                        let cellClass = 'days-breakdown-cell ';
                        if (info) {
                          if (info.type === 'counted') cellClass += 'days-breakdown-counted';
                          else if (info.type === 'weekend') cellClass += 'days-breakdown-weekend';
                          else if (info.type === 'non_working') cellClass += 'days-breakdown-nonworking';
                          else cellClass += 'days-breakdown-holiday';
                        } else {
                          cellClass += 'days-breakdown-out';
                        }
                        return (
                          <div key={dayNum} className={cellClass} title={info ? (info.label || info.type) : ''}>
                            {dayNum}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="days-breakdown-summary mt-3 p-3 rounded bg-light border">
              <strong>Net days of leave:</strong> <span className="text-primary fw-bold">{daysTaken} day{daysTaken !== 1 ? 's' : ''}</span>
              <p className="small text-muted mb-0 mt-1">Only your scheduled working days that are not public holidays count toward your allowance.</p>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DaysTakenBreakdownModal;
